import { useState, useCallback, useRef, useEffect } from "react";
import { useIsTablet } from "./useIsTablet";

// Default chat width percentage (in-memory only)
export const DEFAULT_CHAT_WIDTH = 33.33;
export const TABLET_CHAT_WIDTH = 40;

export interface ChatPanelResize {
  chatWidth: number;
  isDragging: boolean;
  splitLayoutRef: React.RefObject<HTMLDivElement | null>;
  onDragStart: () => void;
  onDragMove: (clientX: number) => void;
  onDragEnd: () => void;
}

/**
 * Translate a pointer position into a constrained panel width and write it
 * straight to the CSS custom property, bypassing React for drag smoothness.
 * Tablet gets tighter constraints so both panels stay usable.
 */
function applyDividerWidth(container: HTMLDivElement, clientX: number, isTablet: boolean): number {
  const rect = container.getBoundingClientRect();
  const newWidthPercent = ((clientX - rect.left) / rect.width) * 100;

  const min = isTablet ? 30 : 20;
  const max = isTablet ? 70 : 80;
  const constrainedWidth = Math.max(min, Math.min(max, newWidthPercent));

  container.style.setProperty("--chat-panel-width", `${constrainedWidth}%`);
  return constrainedWidth;
}

/**
 * Owns the width of the chat panel in the split workspace layout. During a drag
 * the width lives in a ref and the DOM; it is committed to React state only on
 * drag end so the panels do not re-render on every pointer move.
 */
export function useChatPanelResize(): ChatPanelResize {
  const isTablet = useIsTablet();
  const [chatWidth, setChatWidth] = useState<number>(DEFAULT_CHAT_WIDTH);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const chatWidthRef = useRef<number>(DEFAULT_CHAT_WIDTH);
  const dividerRafRef = useRef<number | null>(null);
  const splitLayoutRef = useRef<HTMLDivElement | null>(null);
  const isTabletRef = useRef<boolean>(isTablet);

  // Adjust chat width for tablet (2/5ths) vs desktop (1/3rd)
  useEffect(() => {
    isTabletRef.current = isTablet;
    const targetWidth = isTablet ? TABLET_CHAT_WIDTH : DEFAULT_CHAT_WIDTH;
    setChatWidth(targetWidth);
    chatWidthRef.current = targetWidth;
  }, [isTablet]);

  const onDragMove = useCallback((clientX: number): void => {
    if (dividerRafRef.current !== null) {
      cancelAnimationFrame(dividerRafRef.current);
    }

    dividerRafRef.current = requestAnimationFrame(() => {
      dividerRafRef.current = null;
      if (!splitLayoutRef.current) return;
      chatWidthRef.current = applyDividerWidth(
        splitLayoutRef.current,
        clientX,
        isTabletRef.current
      );
    });
  }, []);

  const onDragEnd = useCallback((): void => {
    if (dividerRafRef.current !== null) {
      cancelAnimationFrame(dividerRafRef.current);
      dividerRafRef.current = null;
    }
    // Commit the ref value to state once, then re-enable transitions
    setChatWidth(chatWidthRef.current);
    setIsDragging(false);
  }, []);

  const onDragStart = useCallback((): void => {
    setIsDragging(true);
  }, []);

  return { chatWidth, isDragging, splitLayoutRef, onDragStart, onDragMove, onDragEnd };
}
